import { useEffect, useState, useMemo, useCallback } from 'react';

interface UseTramitsDataOptions {
  /** Excluir tramits con estat 'Finalitzat' */
  excludeFinalitzat?: boolean;
  /** Limitar el número de resultados */
  limit?: number;
}

/**
 * Hook para obtener y gestionar datos de Tramits.
 *
 * @param apiService - Servicio API que provee `GetTramits()`
 * @param options - Opciones de filtrado y límite
 */
export function useTramitsData(
  apiService: { GetTramits: (entityId: any) => Promise<any[]> },
  entityId: any,
  setSearchLoading: any,
  options?: UseTramitsDataOptions
) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /** Función para cargar los datos */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.GetTramits(entityId);
      setData(result);
      setSearchLoading(false);
    } catch (err: any) {
      console.error('Error obtenint les tramits:', err);
      setError(err.message || 'Error desconegut al obtenir les tramits');
      setSearchLoading(false);
    } finally {
      setLoading(false);
    }
  }, [apiService]);

  /** Cargar al montar o cuando cambie el servicio */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Aplicar filtros y límites */
  const filteredData = useMemo(() => {
    let filtered = [...data];

    if (options?.excludeFinalitzat) {
      filtered = filtered.filter(item => item.estat !== 'Finalitzat');
    }

    if (options?.limit && options.limit > 0) {
      filtered = filtered.slice(0, options.limit);
    }

    return filtered;
  }, [data, options?.excludeFinalitzat, options?.limit]);

  return {
    data: filteredData,
    loading,
    error,
    refetch: fetchData,
  };
}
